import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrendingTopics = ({ onSelectTopic }) => {
  const trendingTopics = [
    {
      id: 'robot-vs-traditional',
      title: 'Robot vs Traditional Vacuums',
      description: 'Complete comparison of automated vs manual cleaning',
      trend: '+127%',
      trendLabel: 'searches this month',
      icon: 'Bot',
      color: 'bg-blue-500',
      tags: ['Technology', 'Comparison', 'Automation'],
      readTime: '15 min',
      difficulty: 'Beginner'
    },
    {
      id: 'cordless-battery-life',
      title: 'Cordless Battery Life Reality Check',
      description: 'Real-world testing vs manufacturer claims',
      trend: '+89%',
      trendLabel: 'interest increase',
      icon: 'Battery',
      color: 'bg-green-500',
      tags: ['Cordless', 'Performance', 'Testing'],
      readTime: '12 min',
      difficulty: 'Intermediate'
    },
    {
      id: 'allergen-removal',
      title: 'Allergen Removal Effectiveness',
      description: 'Scientific testing of HEPA claims and air quality impact',
      trend: '+156%',
      trendLabel: 'seasonal spike',
      icon: 'Shield',
      color: 'bg-purple-500',
      tags: ['Health', 'Allergies', 'HEPA'],
      readTime: '18 min',
      difficulty: 'Advanced'
    },
    {
      id: 'pet-hair-solutions',
      title: 'Ultimate Pet Hair Solutions',
      description: 'Breed-specific testing and specialized attachments',
      trend: '+203%',
      trendLabel: 'user engagement',
      icon: 'Heart',
      color: 'bg-red-500',
      tags: ['Pets', 'Specialized', 'Attachments'],
      readTime: '14 min',
      difficulty: 'Beginner'
    },
    {
      id: 'smart-features',
      title: 'Smart Features Worth Paying For',
      description: 'App connectivity, mapping, and AI features analysis',
      trend: '+78%',
      trendLabel: 'tech adoption',
      icon: 'Smartphone',
      color: 'bg-indigo-500',
      tags: ['Smart Home', 'Apps', 'Innovation'],
      readTime: '16 min',
      difficulty: 'Intermediate'
    },
    {
      id: 'maintenance-costs',
      title: 'Hidden Maintenance Costs',
      description: 'Filter replacements, repairs, and total ownership cost',
      trend: '+94%',
      trendLabel: 'cost concerns',
      icon: 'Wrench',
      color: 'bg-amber-500',
      tags: ['Maintenance', 'Cost', 'Longevity'],
      readTime: '11 min',
      difficulty: 'Beginner'
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Intermediate': return 'text-amber-600 bg-amber-100';
      case 'Advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-accent/10 rounded-full text-accent text-sm font-medium mb-4">
            <Icon name="TrendingUp" size={16} className="mr-2" />
            What's Trending Now
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Hot Topics & Trending Guides
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Stay ahead of the curve with our most popular and rapidly growing guide topics. These are the questions everyone's asking right now.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {trendingTopics?.map((topic) => (
            <div
              key={topic?.id}
              className="bg-white border border-border rounded-xl shadow-subtle hover:shadow-floating transition-all duration-300 overflow-hidden group cursor-pointer"
              onClick={() => onSelectTopic(topic?.id)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`${topic?.color} p-3 rounded-lg`}>
                    <Icon name={topic?.icon} size={24} className="text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-success">{topic?.trend}</div>
                    <div className="text-xs text-text-secondary">{topic?.trendLabel}</div>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-text-primary mb-2 group-hover:text-primary transition-colors">
                  {topic?.title}
                </h3>
                <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                  {topic?.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {topic?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="text-xs bg-muted text-text-secondary px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-3 text-xs text-text-secondary">
                    <span className="flex items-center">
                      <Icon name="Clock" size={12} className="mr-1" />
                      {topic?.readTime}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(topic?.difficulty)}`}>
                      {topic?.difficulty}
                    </span>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="text-primary hover:text-primary/80 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    Read
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            iconName="Flame"
            iconPosition="left"
            onClick={() => onSelectTopic('all-trending')}
          >
            View All Trending Topics
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TrendingTopics;